import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { API_ENDPOINTS } from "../../config/api";

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  card: {
    background: "#fff",
    borderRadius: 10,
    border: "1px solid #e5e7eb",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    overflow: "hidden",
  },
  cardHead: {
    padding: "12px 16px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cardTitle: { fontSize: 13, fontWeight: 600, color: "#111827" },
  cardBody: { padding: "14px 16px" },
  tabBar: {
    display: "flex",
    gap: 4,
    borderBottom: "1px solid #e5e7eb",
    marginBottom: 14,
  },
  tabBtn: (active) => ({
    fontSize: 12,
    fontWeight: 500,
    padding: "7px 14px",
    border: "none",
    background: "none",
    color: active ? "#2563EB" : "#6b7280",
    borderBottom: active ? "2px solid #2563EB" : "2px solid transparent",
    cursor: "pointer",
    marginBottom: -1,
  }),
  label: { fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4 },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 10px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    fontSize: 13,
    color: "#111827",
    resize: "vertical",
    minHeight: 100,
    outline: "none",
    fontFamily: "inherit",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "7px 10px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    fontSize: 13,
    color: "#111827",
    outline: "none",
  },
  fieldGroup: { marginBottom: 14 },
  hintRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 4,
  },
  hintText: { fontSize: 11, color: "#9ca3af" },
  linkBtn: {
    fontSize: 12,
    color: "#2563EB",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
  },
  footerRow: { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 },
  btnSecondary: {
    fontSize: 12,
    fontWeight: 500,
    padding: "6px 14px",
    borderRadius: 6,
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
  },
  btnPrimary: {
    fontSize: 12,
    fontWeight: 500,
    padding: "6px 14px",
    borderRadius: 6,
    border: "none",
    background: "#2563EB",
    color: "#fff",
    cursor: "pointer",
  },
  spinner: {
    display: "inline-block",
    width: 11,
    height: 11,
    border: "2px solid rgba(255,255,255,0.4)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    verticalAlign: "middle",
    marginRight: 5,
  },
  errorBanner: {
    fontSize: 12,
    color: "#991B1B",
    background: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: 7,
    padding: "8px 12px",
    marginBottom: 10,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
  },
  successBanner: {
    fontSize: 12,
    color: "#065F46",
    background: "#D1FAE5",
    border: "1px solid #A7F3D0",
    borderRadius: 7,
    padding: "8px 12px",
    marginBottom: 10,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
  },
  infoBanner: {
    fontSize: 12,
    color: "#374151",
    background: "#F9FAFB",
    border: "1px solid #E5E7EB",
    borderRadius: 7,
    padding: "8px 12px",
    marginBottom: 10,
  },
  summaryRow: {
    display: "flex",
    gap: 6,
    fontSize: 13,
    marginBottom: 5,
    alignItems: "flex-start",
  },
  summaryLabel: { fontWeight: 600, color: "#374151", minWidth: 110, flexShrink: 0 },
  summaryValue: { color: "#6b7280" },
  bulletList: { margin: "4px 0 0 16px", padding: 0, color: "#6b7280", fontSize: 13 },
  editBtn: {
    fontSize: 12,
    fontWeight: 500,
    padding: "5px 12px",
    borderRadius: 6,
    border: "1px solid #2563EB",
    background: "transparent",
    color: "#2563EB",
    cursor: "pointer",
  },
};

// ── Component ──────────────────────────────────────────────────────────────────
const ApplicationReviewTab = ({ application, onApplicationUpdated, readOnly = false }) => {
  const { getAuthHeaders } = useAuth();

  const [activeKey, setActiveKey] = useState("summary");
  const [agentNotes, setAgentNotes] = useState(application.reviewNotes?.agentNotes || "");
  const [creditScore, setCreditScore] = useState(application.reviewNotes?.creditScore ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const reviewNotes = application.reviewNotes || {};
  const backgroundStatus = reviewNotes.backgroundCheck?.status;
  const incomeStatus = reviewNotes.incomeVerification?.status;
  const rentalStatus = reviewNotes.rentalHistory?.status;

  const agentNotesLines = (reviewNotes.agentNotes || "")
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  const handleAddBullet = () => {
    setAgentNotes((prev) => {
      if (!prev) return "- ";
      return prev.endsWith("\n") ? `${prev}- ` : `${prev}\n- `;
    });
  };

  const handleCancel = () => {
    setAgentNotes(application.reviewNotes?.agentNotes || "");
    setCreditScore(application.reviewNotes?.creditScore ?? "");
    setActiveKey("summary");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = {};
      if (agentNotes && agentNotes.trim().length > 0) payload.agentNotes = agentNotes.trim();
      if (creditScore !== "" && creditScore !== null) {
        const parsed = Number(creditScore);
        if (!Number.isNaN(parsed)) payload.creditScore = parsed;
      }
      const res = await fetch(API_ENDPOINTS.applications.updateReview(application._id), {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.message || data?.errors?.[0]?.msg || `Error ${res.status}`);
      }
      const data = await res.json();
      onApplicationUpdated?.(data.data);
      setSuccess("Review notes saved successfully.");
      setActiveKey("summary");
    } catch (err) {
      console.error("Error updating review notes:", err);
      setError(err.message || "Failed to save review notes");
    } finally {
      setSaving(false);
    }
  };

  const currentTab = readOnly ? "summary" : activeKey;

  return (
    <div style={s.card}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Card header */}
      <div style={s.cardHead}>
        <span style={s.cardTitle}>Review Notes</span>
        {!readOnly && (
          <button style={s.editBtn} onClick={() => setActiveKey("edit")}>
            Add / Edit notes
          </button>
        )}
      </div>

      <div style={s.cardBody}>
        {error && (
          <div style={s.errorBanner}>
            <span>{error}</span>
            <button style={{ ...s.linkBtn, color: "#991B1B", fontSize: 14 }} onClick={() => setError(null)}>×</button>
          </div>
        )}
        {success && (
          <div style={s.successBanner}>
            <span>{success}</span>
            <button style={{ ...s.linkBtn, color: "#065F46", fontSize: 14 }} onClick={() => setSuccess(null)}>×</button>
          </div>
        )}
        {readOnly && (
          <div style={s.infoBanner}>
            Review notes cannot be edited because the linked property was deleted.
          </div>
        )}

        {/* Tab bar */}
        <div style={s.tabBar}>
          <button style={s.tabBtn(currentTab === "summary")} onClick={() => !readOnly && setActiveKey("summary")}>
            Summary
          </button>
          {!readOnly && (
            <button style={s.tabBtn(currentTab === "edit")} onClick={() => setActiveKey("edit")}>
              Edit notes
            </button>
          )}
        </div>

        {/* Summary tab */}
        {currentTab === "summary" && (
          <div>
            <div style={s.summaryRow}>
              <span style={s.summaryLabel}>Agent notes:</span>
              <span style={s.summaryValue}>
                {agentNotesLines.length === 0 ? (
                  "No agent notes added yet."
                ) : (
                  <ul style={s.bulletList}>
                    {agentNotesLines.map((line, idx) => (
                      <li key={idx}>{line.replace(/^-\s*/, "")}</li>
                    ))}
                  </ul>
                )}
              </span>
            </div>
            <div style={s.summaryRow}>
              <span style={s.summaryLabel}>Credit score:</span>
              <span style={s.summaryValue}>
                {reviewNotes.creditScore != null ? reviewNotes.creditScore : "-"}
              </span>
            </div>
            {backgroundStatus && backgroundStatus !== "pending" && (
              <div style={s.summaryRow}>
                <span style={s.summaryLabel}>Background:</span>
                <span style={s.summaryValue}>{backgroundStatus}</span>
              </div>
            )}
            {incomeStatus && incomeStatus !== "pending" && (
              <div style={s.summaryRow}>
                <span style={s.summaryLabel}>Income check:</span>
                <span style={s.summaryValue}>{incomeStatus}</span>
              </div>
            )}
            {rentalStatus && rentalStatus !== "pending" && (
              <div style={s.summaryRow}>
                <span style={s.summaryLabel}>Rental history:</span>
                <span style={s.summaryValue}>{rentalStatus}</span>
              </div>
            )}
          </div>
        )}

        {/* Edit tab */}
        {currentTab === "edit" && (
          <form onSubmit={handleSubmit}>
            <div style={s.fieldGroup}>
              <div style={s.label}>Agent review notes (point form)</div>
              <textarea
                style={s.textarea}
                rows={5}
                value={agentNotes}
                onChange={(e) => setAgentNotes(e.target.value)}
                placeholder={`- Good income and stable job\n- Awaiting background check\n- Follow up with previous landlord`}
                disabled={readOnly}
              />
              <div style={s.hintRow}>
                <button type="button" style={s.linkBtn} onClick={handleAddBullet} disabled={readOnly}>
                  Add bullet
                </button>
                <span style={s.hintText}>Use one line per point; bullets are optional.</span>
              </div>
            </div>

            <div style={s.fieldGroup}>
              <div style={s.label}>Credit score (optional)</div>
              <input
                style={s.input}
                type="number"
                min="0"
                value={creditScore}
                onChange={(e) => setCreditScore(e.target.value)}
                placeholder="e.g. 720"
                disabled={readOnly}
              />
            </div>

            <div style={s.footerRow}>
              <button type="button" style={s.btnSecondary} onClick={handleCancel} disabled={saving}>
                Cancel
              </button>
              <button type="submit" style={s.btnPrimary} disabled={saving || readOnly}>
                {saving ? (
                  <>
                    <span style={s.spinner} />
                    Saving…
                  </>
                ) : (
                  "Save notes"
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ApplicationReviewTab;
